import { Controller } from "@hotwired/stimulus";

const MIN_WIDTH = 420; // floor = the chat UI's inner min-width; the panel can't shrink below its default
const MAIN_MIN_WIDTH = 640; // the main content area never gets narrower than this ("mobile size")
const STORAGE_KEY = "massimo-chat-width";

// Drag-to-resize for the Mo chat sidebar. The open width is applied through the
// `--mo-chat-width` custom property (see the `#massimo-chat-toggle:checked ~ .mo-sidebar`
// rule in application.tailwind.css); this controller keeps that property in sync with the
// drag handle and clamps it so the main content stays at least MAIN_MIN_WIDTH wide.
export default class extends Controller {
  static targets = ["handle"];

  connect() {
    this.sidebar = this.element.querySelector(".mo-sidebar");
    this.width = MIN_WIDTH;
    this.preferredWidth = MIN_WIDTH;
    this.hasPreference = false;
    this.restore();
    window.addEventListener("resize", this.onResize);
    // Exiting fullscreen restores the saved width; re-clamp in case the viewport shrank
    // while fullscreen (when #main-container is hidden and can't be measured).
    this.observer = new MutationObserver(this.reclamp);
    this.observer.observe(this.element, { attributes: true, attributeFilter: ["data-fullscreen"] });
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize);
    this.observer?.disconnect();
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
  }

  // The sidebar and #main-container share a fixed amount of space (everything inside
  // .admin-page minus the side nav), so `current sidebar + current main` is constant
  // whether the panel is open or closed. Subtracting the main-content minimum gives the
  // widest the sidebar may grow.
  maxWidth() {
    const main = document.getElementById("main-container")?.getBoundingClientRect().width ?? 0;
    const current = this.sidebar.getBoundingClientRect().width;
    return current + main - MAIN_MIN_WIDTH;
  }

  clampWidth(px, max = this.maxWidth()) {
    return Math.max(MIN_WIDTH, Math.min(max, px));
  }

  // `animate: false` suppresses the width transition for one frame so restoring or
  // re-clamping a width doesn't visibly slide.
  setWidth(px, animate = true) {
    this.width = px;
    if (animate) {
      this.element.style.setProperty("--mo-chat-width", `${px}px`);
    } else {
      this.sidebar.style.transition = "none";
      this.element.style.setProperty("--mo-chat-width", `${px}px`);
      requestAnimationFrame(() => (this.sidebar.style.transition = ""));
    }
  }

  restore() {
    const stored = parseInt(localStorage.getItem(STORAGE_KEY), 10);
    if (!Number.isFinite(stored)) return;
    this.preferredWidth = stored;
    this.hasPreference = true;
    this.setWidth(this.clampWidth(stored), false);
  }

  start = (event) => {
    if (event.button !== 0 || this.element.hasAttribute("data-fullscreen")) return;
    event.preventDefault();
    this.handleTarget.setPointerCapture(event.pointerId);
    this.startX = event.clientX;
    this.startWidth = this.sidebar.getBoundingClientRect().width;
    this.max = this.maxWidth(); // cached: the side nav and viewport don't change mid-drag
    this.sidebar.style.transition = "none"; // track the pointer 1:1, no easing lag
    document.body.classList.add("column-gesture-active", "column-gesture-resize");
    this.handleTarget.addEventListener("pointermove", this.move);
    this.handleTarget.addEventListener("pointerup", this.end);
  };

  move = (event) => {
    const next = this.clampWidth(this.startWidth + event.clientX - this.startX, this.max);
    this.width = next;
    this.element.style.setProperty("--mo-chat-width", `${next}px`);
  };

  end = (event) => {
    this.handleTarget.releasePointerCapture(event.pointerId);
    this.handleTarget.removeEventListener("pointermove", this.move);
    this.handleTarget.removeEventListener("pointerup", this.end);
    document.body.classList.remove("column-gesture-active", "column-gesture-resize");
    this.sidebar.style.transition = "";
    this.preferredWidth = this.width;
    this.hasPreference = true;
    localStorage.setItem(STORAGE_KEY, Math.round(this.width));
  };

  onResize = () => this.reclamp();

  // Keep the chosen width valid as the layout changes: shrink it if it would push the
  // main content below its minimum, and grow it back toward the user's preference when
  // room returns. No-op until the user has set a width.
  reclamp = () => {
    if (!this.hasPreference || this.resizeFrame) return;
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null;
      const target = this.clampWidth(this.preferredWidth);
      if (Math.round(target) !== Math.round(this.width)) this.setWidth(target, false);
    });
  };
}
